import constate from "constate";
import produce, { Draft } from "immer";
import { useCallback, useEffect, useReducer } from "react";
import { useHistory, useLocation } from "react-router-dom";

export type Locator = string;

type HistoryPointer = string;
type HistoryEvent = {
  id: HistoryPointer;
  path: string;
};
type HistoryEntry = {
  id: HistoryPointer;
  path: string;
  fallback: string | null;
};
type HistoryAction =
  | {
      type: "PUSH" | "REPLACE";
      payload: HistoryEvent;
    }
  | {
      type: "POP";
      payload: HistoryEvent;
      // meta: {
      //   direction: "FORWARD" | "BACKWARD";
      // };
    }
  | {
      type: "FALLBACK";
      payload: string | null;
    };

type State = {
  history: HistoryEntry[];
  current: HistoryPointer | null;
};

const locatorReducer = produce((draft: Draft<State>, action: HistoryAction) => {
  const currentIndex = draft.history.findIndex(
    (item) => item.id === draft.current,
  );

  switch (action.type) {
    // NOTE: This can be caused by EITHER "forward" OR "backward" buttons/actions
    case "POP":
      const targetIndex = draft.history.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (targetIndex !== -1) {
        // item actually exists in our history stack, so just readjust the pointer
        draft.current = action.payload.id;
      } else {
        // item does not exist for some reason
        console.error("Your action did not specify the travel direction.");
      }
      break;
    case "PUSH":
      draft.history = draft.history.slice(0, currentIndex + 1);
      draft.history.push({
        ...action.payload,
        fallback: null,
      });
      draft.current = action.payload.id;
      break;
    case "REPLACE":
      if (draft.current) {
        draft.history[currentIndex].id = action.payload.id;
        draft.history[currentIndex].path = action.payload.path;
        draft.current = action.payload.id;
      } else {
        // this is basically the init on a fresh page load
        draft.history.push({
          ...action.payload,
          fallback: null,
        });
        draft.current = action.payload.id;
      }
      break;
    case "FALLBACK":
      if (draft.current) {
        draft.history[currentIndex].fallback = action.payload;
      }
      break;
  }

  return draft;
});

export const useHistoryManagerInstance = () => {
  const history = useHistory();
  const location = useLocation();

  const [state, dispatch] = useReducer(locatorReducer, {
    history: [],
    current: null,
  });

  // register listener
  useEffect(() => {
    // this return ensures that on unmount it would remove the listener
    return history?.listen((location, action) => {
      dispatch({
        type: action,
        payload: {
          id: window.history.state?.key,
          path: location.pathname,
        },
      });
    });
  }, [history]);

  // On mount, fire a one-off replace which triggers the react router's default
  // behaviour and gives this route a history entry with a unique key that we need.
  useEffect(() => {
    if (process.env.NODE_ENV !== "test") {
      /* istanbul ignore next */
      history.replace(location.pathname, location.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose "selectors"
  const getFallbackPath: () => string | null = useCallback(() => {
    return (
      state.history.find((item) => item.id === state.current)?.fallback ?? null
    );
  }, [state]);

  const getCurrentPath: () => string | null = useCallback(() => {
    return (
      state.history.find((item) => item.id === state.current)?.path ?? null
    );
  }, [state]);

  const getPreviousPath: () => string | null = useCallback(() => {
    const currentIndex = state.history.findIndex(
      (item) => item.id === state.current,
    );
    return state.history[currentIndex - 1]?.path ?? null;
  }, [state]);

  // Expose fallback setter
  const setFallbackUrl = useCallback((url: Locator | null) => {
    dispatch({ type: "FALLBACK", payload: url });
  }, []);

  return {
    setFallbackUrl,
    current: getCurrentPath(),
    previous: getPreviousPath(),
    fallback: getFallbackPath(),
  };
};

export const [
  AppHistoryProvider,
  useAppHistoryState,
  useSetFallbackUrl,
] = constate(
  useHistoryManagerInstance,
  ({ current, previous, fallback }) => ({ current, previous, fallback }),
  ({ setFallbackUrl }) => setFallbackUrl,
);
